import { useCallback, useEffect, useRef, useState } from 'react';

export type CopyState = 'idle' | 'copied' | 'failed';

/** What a copy button says when the browser would not copy: the way that always works. */
export const COPY_BY_HAND = 'Could not copy: select and copy it by hand.';

/**
 * Copying to the clipboard, with what happened: the browser refuses when the page is not focused,
 * the permission is denied or the address is not secure, and a button that then says nothing (or
 * "Copied") leaves a person pasting something else. `copied` goes back to idle after `resetMs`,
 * when one is given; `failed` stays until the next try.
 */
export function useCopy(value: string, options: { resetMs?: number; onCopied?: () => void } = {}) {
  const { resetMs, onCopied } = options;
  const [state, setState] = useState<CopyState>('idle');
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );
  const copy = useCallback(async (): Promise<void> => {
    let next: CopyState = 'copied';
    try {
      await navigator.clipboard.writeText(value);
      onCopied?.();
    } catch {
      next = 'failed';
    }
    setState(next);
    if (timer.current !== null) window.clearTimeout(timer.current);
    if (resetMs !== undefined && next === 'copied')
      timer.current = window.setTimeout(() => setState('idle'), resetMs);
  }, [value, resetMs, onCopied]);
  return { state, copy };
}
